import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";

import Home from "./pages/Home";
import Report from "./pages/Report";
import Nomatch from "./pages/Nomatch";
import AppLayout from "./Layout/AppLayout";
import { Category, Transaction, User } from "./types";
import { auth } from "./firebase";
import { formatMonth } from "./utils/formatting";
import { useMediaQuery } from "@mui/material";
import Login from "./pages/Login";
import { env } from "./utils/environment";
import request from "./apis/request";
import { APIResponse } from "./types/apiTypes";
import { onAuthStateChanged, signOut, onIdTokenChanged } from "firebase/auth";
import Register from "./pages/Register";
import Loading from "./components/common/Loading";
import ChangePassword from "./pages/ChangePassword";
import Setting from "./pages/Setting";
import { useDispatch, useSelector } from "react-redux";
import {
  setWholeTransaction,
  setTransaction,
  setIsLoggedIn,
  setUserInfo,
  setIsMobile,
  setUserId,
  setCategory,
} from "./store/appSlice";
import { RootState } from "./store/store";
import { defaultCategory } from "./components/common/Category";
import { AlertMessage } from "./components/AlertMessage";
import i18n from "./i18n";
import { useTranslation } from "react-i18next";
import { changeCategoryLabel } from "./utils/utilities";

function App() {
  const {
    wholeTransactions,
    currentMonth,
    userInfo,
    isLoggedIn,
    baseLanguage,
  } = useSelector((state: RootState) => state.app);
  const dispatch = useDispatch();
  const [initializing, setInitializing] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { t: tCommon } = useTranslation("common");

  const theme = useTheme();
  // 1200Pixel以下でtrueになる
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  dispatch(setIsMobile(isMobile));

  useEffect(() => {
    // 画面を表示じにfirebaseがローカル保存データを確認してログイン状態を復旧が実行する
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        dispatch(setIsLoggedIn(false));
        dispatch(setUserInfo(null));
        setInitializing(false);
        return;
      }

      const idToken = await currentUser.getIdToken();
      const authUser = {
        id: "",
        authedId: currentUser.uid,
        email: currentUser.email,
        name: currentUser.displayName,
        provider: currentUser.providerId,
        token: idToken,
        options: currentUser,
      } as User;

      // for dev and debug
      if (env.REACT_APP_MODE !== "production") {
        const tokenResult = await currentUser.getIdTokenResult();
        const issTime = new Date(tokenResult.issuedAtTime);
        const expTime = new Date(tokenResult.expirationTime);
        console.log(
          `--sa0010--セッション復旧開始 - email:${currentUser.email}, token: ${idToken}`,
        );
        console.log(
          `--sa0011--token 発行: ${issTime.toLocaleString()}, 有効期限： ${expTime.toLocaleString()}`,
        );
      }

      dispatch(setUserInfo(authUser));
      setInitializing(false); // セッション復旧完了

      // ユーザ情報取得
      const setInitialLoggedIn = async () => {
        if (currentUser.email) {
          try {
            const response = await request<APIResponse<User, string>>({
              method: "GET",
              url: "userInfo",
              params: { email: currentUser.email },
              requireAuth: true,
              token: idToken,
            });

            const { success, message, data } = response.data;
            console.log(
              `--sa0031-- get user info: success:${success}, message:${message}, data:${JSON.stringify(data)}`,
            );

            dispatch(setUserId(data.id));
            dispatch(setIsLoggedIn(true));
          } catch (error) {
            console.error("ユーザ情報エラー", error);
          }
        }
      };

      setInitialLoggedIn();
      setInitializing(false);
    });

    // 2. トークン有効期限切れ対策のリスナー（今回のコア修正）
    //    1時間の有効期限が切れる前に、Firebase SDKが裏で自動更新したタイミングを検知します
    const unsubscribeToken = onIdTokenChanged(auth, async (currentUser) => {
      if (currentUser) {
        const newToken = await currentUser.getIdToken();
        const authUser = {
          ...userInfo,
          authedId: currentUser.uid,
          email: currentUser.email,
          name: currentUser.displayName,
          provider: currentUser.providerId,
          token: newToken,
          options: currentUser,
        } as User;

        dispatch(setUserInfo(authUser));

        // for dev and debug
        if (env.REACT_APP_MODE !== "production") {
          const tokenResult = await currentUser.getIdTokenResult();
          const issTime = new Date(tokenResult.issuedAtTime);
          const expTime = new Date(tokenResult.expirationTime);

          console.log(
            `--sa0010--セッション復旧開始 - email:${authUser.email}, token: ${authUser.token}`,
          );
          console.log(
            `--sa0011--token 発行: ${issTime.toLocaleString()}, 有効期限： ${expTime.toLocaleString()}`,
          );
        }
      }
    });

    // コンポネントのUnmount時にリスナーを解除
    return () => {
      unsubscribe();
      unsubscribeToken();
    };
  }, []);

  //初回レンダーリング時のみfirestoreから取得
  useEffect(() => {
    if (!isLoggedIn) {
      dispatch(setWholeTransaction([]));
      dispatch(setCategory(defaultCategory));
      return;
    }
    const fetchCategory = async (token = "") => {
      console.log("fetched category-request-:", token);
      try {
        // const response = await request<APIResponse<Category>>({
        //   method: "PUT",
        //   url: "updateCategory",
        //   // method: "POST",
        //   // url: "createCategory",
        //   requireAuth: true,
        //   data: defaultCategory,
        //   token,
        // });
        const response = await request<APIResponse<Category, string>>({
          method: "GET",
          url: "getCategory",
          requireAuth: true,
          token,
        });

        const { success, message, data } = response.data;
        console.log("fetched category:", success, message, data);

        dispatch(
          setCategory(
            changeCategoryLabel(userInfo?.language || baseLanguage, data),
          ),
        );
      } catch (error) {
        console.error(`取引分類カテゴリ取得中 error, ${error}`);
      }
    };

    const fetchTransactions = async (token: string) => {
      try {
        const response = await request<APIResponse<Transaction[]>>({
          method: "GET",
          url: "getUserTransactions",
          requireAuth: true,
          token,
        });
        const { success, message, data } = response.data;
        console.log("fetched transactions:", success, message, data);

        dispatch(setWholeTransaction(data));
      } catch (error) {
        if (env.REACT_APP_EMULATOR_MODE) {
          console.warn(
            "[app] emulator mode is enabled, but the transaction callable could not be reached. Continuing with an empty list.",
            error,
          );
          // } else if (isFireStoreError(error)) {
          //   console.error(`DB error, ${error.code}, ${error.message}`);
        } else {
          console.error(`取引履歴取得中 error, ${error}`);
        }
        dispatch(setWholeTransaction([]));
      }
    };

    if (isLoggedIn) {
      try {
        setIsFetching(true);

        fetchTransactions(userInfo?.token || "");

        fetchCategory(userInfo?.token);
      } catch (error) {
      } finally {
        setIsFetching(false);
      }
    } else {
      dispatch(setWholeTransaction([]));
      dispatch(setCategory(defaultCategory));
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const monthlyTransactions = wholeTransactions.filter((t) => {
      return t.date.startsWith(formatMonth(new Date(currentMonth)));
    });
    dispatch(setTransaction(monthlyTransactions));
  }, [currentMonth, wholeTransactions]);

  useEffect(() => {
    if (isLoggedIn) {
      console.log(
        "selected language,currency:",
        userInfo?.language,
        userInfo?.currency,
      );
      if (userInfo?.language) {
        i18n.changeLanguage(userInfo.language);
      }
    }
  }, [userInfo?.language, userInfo?.currency]);

  const handleLogout = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    console.log("logout");

    setShowPopup(true);

    // if (!window.confirm("ログアウトしますか")) {
    //   return;
    // }

    // signOut(auth);

    // console.log("log out する");

    // dispatch(setIsLoggedIn(false));
    // dispatch(setUserInfo(null));
  };
  const [showPopup, setShowPopup] = useState(false);

  const handleAlertClose = (type: string) => {
    if (type === "logout") {
      signOut(auth);
      dispatch(setIsLoggedIn(false));
      dispatch(setUserInfo(null));
    }
    setShowPopup(false);
  };
  const handleCloseAlert = () => {
    setShowPopup(false);
  };

  return (
    <>
      <Router>
        <Routes>
          <Route path="/" element={<AppLayout handleLogout={handleLogout} />}>
            <Route index element={<Home isFetching={isFetching} />} />
            <Route path="/report" element={<Report />} />
            <Route path="/setting" element={<Setting />} />

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="*" element={<Nomatch />} />
          </Route>
        </Routes>
        {initializing && <Loading message={tCommon("messages.loading")} />}
        {isProcessing && (
          <Loading
            message={tCommon("messages.loadingTransaction")}
            blurBackdrop={true}
          />
        )}
        {isFetching && (
          <Loading
            message={tCommon("messages.loadingCategory")}
            blurBackdrop={true}
          />
        )}
      </Router>
      <AlertMessage
        open={showPopup}
        type={"warn"}
        returnType={"logout"}
        errorCode="alert.Logout"
        onResult={handleAlertClose}
        handleClose={handleCloseAlert}
      ></AlertMessage>
    </>
  );
}

export default App;
